import { useState, useRef, useCallback, useEffect } from 'react'
import { useAppStore } from '@/store/appStore'
import { resolveUrl } from '@/config'

export function SectionsDrawer() {
  // console.log('🎛️ [SectionsDrawer] COMPONENT CALLED - checking render conditions')
  const sections = useAppStore(state => state.sections)
  const currentSectionId = useAppStore(state => state.currentSectionId)
  const cameras = useAppStore(state => state.cameras)
  const shopId = useAppStore(state => state.currentShopId)
  const loadSectionAsIndependentShop = useAppStore(state => state.loadSectionAsIndependentShop)
  const isLoadingData = useAppStore(state => state.isLoadingData)
  const controlsVisible = useAppStore(state => state.controlsVisible)
  const availableDatasets = useAppStore(state => state.availableDatasets)
  
  // DEBUG: Log sections when they change
  useEffect(() => {
    if (sections && sections.length > 0) {
      console.log(`📦 [SectionsDrawer] Detected ${sections.length} sections for shopId: ${shopId}`)
      sections.forEach((s, i) => {
        console.log(`  Section ${i}: ID=${s.sectionId}, frames ${s.startFrame}-${s.endFrame} (${s.frameCount} frames)`)
      })
    }
  }, [sections, shopId])
  
  const [snapPoint, setSnapPoint] = useState<number>(0.32) // Start at 0.32 (middle snap)
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const [currentY, setCurrentY] = useState(0)
  const [hasMoved, setHasMoved] = useState(false)
  const [dragStartSnapPoint, setDragStartSnapPoint] = useState(0.45)
  const dragThreshold = 10 // Minimum distance to start dragging
  const drawerRef = useRef<HTMLDivElement>(null)
  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  
  // Auto-hide drawer after 5 seconds when at middle position
  const resetAutoHideTimer = useCallback(() => {
    // Clear existing timeout
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current)
      autoHideTimeoutRef.current = null
    }
    
    // Only start timer if at middle position (0.32 or 0.45)
    if (snapPoint === 0.32 || snapPoint === 0.45) {
      autoHideTimeoutRef.current = setTimeout(() => {
        console.log('⏰ [SectionsDrawer] Auto-hiding drawer after 5 seconds')
        setSnapPoint(0.1) // Minimize drawer
      }, 5000)
    }
  }, [snapPoint])
  
  // Start/restart timer when snapPoint changes
  useEffect(() => {
    resetAutoHideTimer()
    
    // Cleanup on unmount
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current)
      }
    }
  }, [resetAutoHideTimer])
  
  // // DEBUG: Log snapPoint changes
  // useEffect(() => {
  //   console.log(`🎛️ [SectionsDrawer] snapPoint changed to: ${snapPoint}`)
  // }, [snapPoint])
  
  // Get shop metadata to access camera_count for sorting
  const shopMetadata = useAppStore(state => state.shopMetadata)
  
  // TEST: Set to true to test with 40 dummy sections
  const TEST_MODE = false
  const displaySections = TEST_MODE ? Array.from({ length: 40 }, (_, i) => ({
    sectionId: i,
    startFrame: i * 5,
    endFrame: (i + 1) * 5 - 1,
    frameCount: 5,
    frames: Array.from({ length: 5 }, (_, j) => i * 5 + j)
  })) : [...sections].sort((a, b) => {
    // Sort by camera count (descending) - requires shopMetadata
    if (shopMetadata) {
      const sectionA = shopMetadata.sections.find(s => s.section_id === a.sectionId)
      const sectionB = shopMetadata.sections.find(s => s.section_id === b.sectionId)
      if (sectionA && sectionB) {
        return sectionB.camera_count - sectionA.camera_count
      }
    }
    // Fallback to section ID order if metadata not available
    return a.sectionId - b.sectionId
  })
  
  // console.log('🎛️ [SectionsDrawer] displaySections:', {
  //   length: displaySections.length,
  //   TEST_MODE,
  //   willRender: !(isLoadingData || displaySections.length === 0)
  // })
  
  // Note: Parent component (GestureViewer) guards against rendering this component
  // when loading, so we don't need early return here. All hooks must run unconditionally.
  
  const getThumbnailPath = (shopId: string | null, sectionId: number) => {
    if (!shopId) return ''
    
    // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
    const dataset = availableDatasets.find(d => d.id === shopId)
    const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
    
    // For 0-indexed isolated sections, use the standard thumbnail (frame_0000.jpg)
    // which is created as a copy of the first frame in each section
    const framesPath = `${datasetBasePath}/section_${sectionId}/frames`
    
    // Always use frame_0000.jpg as the thumbnail for each section
    return resolveUrl(`${framesPath}/frame_0000.jpg?shop=${shopId}`)
  }

  const handleSectionClick = useCallback((sectionId: number) => {
    if (!shopId) {
      // console.error('❌ [SectionsDrawer] No shopId available')
      return
    }
    console.log(`
╔════════════════════════════════════════════════════════════════╗
║ 🎬 [SectionsDrawer] SECTION ${sectionId} CLICKED                        
║────────────────────────────────────────────────────────────────║
║ shopId: ${shopId}
║ sectionId: ${sectionId}
║ Action: Loading section as independent shop
╚════════════════════════════════════════════════════════════════╝
    `)
    loadSectionAsIndependentShop(shopId, sectionId).then(() => {
      console.log(`✅ [SectionsDrawer] Section ${sectionId} loaded successfully`)
      // 🎬 Auto-play all frames in the selected section
      const appState = useAppStore.getState()
      appState.playAllFramesInSection()
    }).catch((err: Error) => {
      console.error(`❌ [SectionsDrawer] Failed to load section ${sectionId}:`, err)
    })
    setSnapPoint(0.32) // Snap to middle position when section is selected
    // Reset auto-hide timer when section is clicked
    resetAutoHideTimer()
  }, [loadSectionAsIndependentShop, shopId, resetAutoHideTimer])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touchY = e.touches[0].clientY
    setStartY(touchY)
    setCurrentY(touchY)
    setHasMoved(false)
    setDragStartSnapPoint(snapPoint) // Remember where we started dragging from
    // Reset auto-hide timer on user interaction
    resetAutoHideTimer()
  }, [snapPoint, resetAutoHideTimer])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const mouseY = e.clientY
    setStartY(mouseY)
    setCurrentY(mouseY)
    setHasMoved(false)
    setDragStartSnapPoint(snapPoint)
    setIsDragging(true)
    // Reset auto-hide timer on user interaction
    resetAutoHideTimer()
  }, [snapPoint, resetAutoHideTimer])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touchY = e.touches[0].clientY
    setCurrentY(touchY)
    
    // Check if user has moved enough to start dragging
    if (!hasMoved && Math.abs(touchY - startY) > dragThreshold) {
      setHasMoved(true)
      setIsDragging(true)
    }
  }, [hasMoved, startY, dragThreshold])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return
    const mouseY = e.clientY
    setCurrentY(mouseY)
    
    if (!hasMoved && Math.abs(mouseY - startY) > dragThreshold) {
      setHasMoved(true)
    }
  }, [isDragging, hasMoved, startY, dragThreshold])

  const handleTouchEnd = useCallback(() => {
    if (!hasMoved) {
      // This was a tap, not a drag - cycle snap points
      if (snapPoint === 0.45) setSnapPoint(1.0)
      else if (snapPoint === 1.0) setSnapPoint(0.1)
      else setSnapPoint(0.45)
  } else if (isDragging) {
    // This was a drag - snap to nearest point based on current drag position
    const snapPoints = [0.1, 0.45, 1.0]
    const newSnapPoint = snapPoints.reduce((prev, curr) => 
      Math.abs(curr - currentSnapPoint) < Math.abs(prev - currentSnapPoint) ? curr : prev
    )
    
    setSnapPoint(newSnapPoint)
  }    setIsDragging(false)
    setHasMoved(false)
    setCurrentY(0)
    setStartY(0)
  }, [hasMoved, isDragging, currentY, startY, dragStartSnapPoint, snapPoint])

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return
    
    if (!hasMoved) {
      // This was a click, not a drag - cycle snap points
      if (snapPoint === 0.45) setSnapPoint(1.0)
      else if (snapPoint === 1.0) setSnapPoint(0.1)
      else setSnapPoint(0.45)
    } else {
      // This was a drag - snap to nearest point
      const drawerHeight = drawerRef.current?.clientHeight || window.innerHeight * 0.8
      const dragDelta = (currentY - startY) / drawerHeight
      const draggedSnapPoint = Math.max(0, Math.min(1, dragStartSnapPoint - dragDelta))
      
      const snapPoints = [0.1, 0.45, 1.0]
      const newSnapPoint = snapPoints.reduce((prev, curr) => 
        Math.abs(curr - draggedSnapPoint) < Math.abs(prev - draggedSnapPoint) ? curr : prev
      )
      setSnapPoint(newSnapPoint)
    }
    
    setIsDragging(false)
    setHasMoved(false)
    setCurrentY(0)
    setStartY(0)
  }, [isDragging, hasMoved, snapPoint, currentY, startY, dragStartSnapPoint])
  
  // Add/remove mouse move and mouse up listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Calculate transform based on current drag state
  const drawerHeight = drawerRef.current?.clientHeight || window.innerHeight * 0.8 // fallback to 80vh
  const dragDelta = isDragging && hasMoved ? (currentY - startY) / drawerHeight : 0
  const currentSnapPoint = isDragging && hasMoved
    ? Math.max(0, Math.min(1, dragStartSnapPoint - dragDelta))
    : snapPoint
  const transform = `translateY(${(1 - currentSnapPoint) * 100}%)`
  
  // Calculate padding needed for scrolling at different snap points
  // The visible drawer area at any snap point = currentSnapPoint * drawerHeight
  // To scroll to bottom content, we need padding equal to the hidden portion
  const visibleHeight = drawerHeight * currentSnapPoint
  const hiddenHeight = drawerHeight * (1 - currentSnapPoint)
  // Account for handle (~56px) and footer (~34px) which take space but don't scroll
  const fixedElementsHeight = 90
  const scrollableVisibleHeight = Math.max(0, visibleHeight - fixedElementsHeight)
  
  // Calculate how many items are visible per row to determine if we need more padding
  const itemsPerRow = Math.floor((window.innerWidth - 16) / 88) || 1
  const totalSections = displaySections.length
  const totalRows = Math.ceil(totalSections / itemsPerRow)
  const itemHeight = 120 // Height of each section item
  const gap = 0 // Gap between items
  const totalContentHeight = totalRows * (itemHeight + gap)
  
  // Base padding = hidden height
  // Add extra padding if we have multiple columns (less content height relative to container)
  const basePadding = hiddenHeight
  const extraPaddingFactor = itemsPerRow > 1 ? 1.5 : 1 // Add 50% more padding when multiple columns
  const paddingNeeded = Math.max(0, basePadding * extraPaddingFactor)
  
  // console.log(`🎛️ [SectionsDrawer] RENDER: snapPoint=${snapPoint}, currentSnapPoint=${currentSnapPoint}, controlsVisible=${controlsVisible}, isDrawerHidden=${!controlsVisible && currentSnapPoint === 0.1}`)
  // console.log(`🎛️ [SectionsDrawer] Padding: ${paddingNeeded.toFixed(0)}px (itemsPerRow=${itemsPerRow}, totalRows=${totalRows}, contentHeight=${totalContentHeight}px)`)
  
  // Hide drawer when controls are hidden and at 0.1 snap point
  const isDrawerHidden = !controlsVisible && currentSnapPoint === 0.1
  
  // Don't render drawer at all when it should be hidden
  if (isDrawerHidden) {
    return null
  }

  return (
    <div 
      ref={drawerRef}
      className="sections-drawer__container"
      style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        zIndex: 999999,
        transform,
        transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(4px)', // Reduced blur for better visibility
        borderTop: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: '12px 12px 0 0',
        maxHeight: '85vh',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Drag handle */}
      <div 
        className="sections-drawer__handle"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        style={{
          width: '86px', // Larger touch area
          height: '40px', // Larger touch area
          background: 'transparent', // Invisible background
          margin: '8px auto 8px auto', // Match top gap
          cursor: 'pointer',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Visual handle */}
        <div style={{
          width: '70px',
          height: '24px',
          background: 'rgba(128, 128, 128, 0.3)', // Light gray background
          border: '2px solid rgba(255, 255, 255, 0.8)', // White border
          borderRadius: '12px'
        }}></div>
      </div>
      
      {/* Sections grid */}
      <div 
        className="sections-drawer__list"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: '0px',
          padding: '0px 8px',
          overflowY: 'auto',
          flex: '1 1 0',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y'
        }}
        onTouchStart={(e) => {
          e.stopPropagation()
          resetAutoHideTimer() // Reset timer on scroll start
        }}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onScroll={() => resetAutoHideTimer()} // Reset timer on scroll
      >
        {displaySections.map((section, index) => {
          const isActive = section.sectionId === currentSectionId

          // Get thumbnail URL - use frame_0000.jpg for each section
          const thumbnailUrl = getThumbnailPath(shopId, section.sectionId)

          return (
            <button
              key={section.sectionId}
              className={`section-item ${isActive ? 'section-item--active' : ''}`}
              onClick={() => handleSectionClick(section.sectionId)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0px',
                padding: '0px',
                background: isActive ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                border: `3px solid ${isActive ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.08)'}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                fontFamily: 'inherit',
                color: 'white',
                height: '18vh', // Reduced card height
              }}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {thumbnailUrl ? (
                  <>
                    <img
                      src={thumbnailUrl}
                      alt={`Section ${index + 1}`}
                      onError={(e) => {
                        // Fallback if thumbnail fails to load
                        const img = e.target as HTMLImageElement
                        img.style.display = 'none'
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        background: 'rgba(0, 0, 0, 0.3)'
                      }}
                    />
                    <div style={{ 
                      position: 'absolute', 
                      bottom: '0', 
                      left: '0', 
                      right: '0', 
                      background: 'rgba(0, 0, 0, 0.5)', 
                      color: 'white', 
                      padding: '4px 8px 2px 8px', 
                      textAlign: 'center',
                      borderRadius: '0 0 6px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontSize: '10px', fontWeight: '600', marginBottom: '1px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isActive && (
                            <a
                              href="/3d-viewer/index.html"
                              target="_blank"
                              onClick={(e) => {
                                e.stopPropagation()
                              }}
                              style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(0, 255, 0, 0.4)',
                                borderRadius: '4px',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                transition: 'all 0.2s',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'
                                e.currentTarget.style.borderColor = 'rgba(0, 255, 0, 0.6)'
                                e.currentTarget.style.transform = 'scale(1.1)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                                e.currentTarget.style.borderColor = 'rgba(0, 255, 0, 0.4)'
                                e.currentTarget.style.transform = 'scale(1)'
                              }}
                              title="Open 3D Viewer"
                            >
                              🎲
                            </a>
                          )}
                          <span>Section {index + 1}</span>
                        </div>
                        <div style={{ fontSize: '8px', opacity: 0.8 }}>
                          {section.frameCount} frames
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Placeholder for sections not yet loaded
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(128, 128, 128, 0.2)',
                    borderRadius: '6px'
                  }}>
                    <div style={{ fontSize: '14px', marginBottom: '4px' }}>📌</div>
                    <div style={{ fontSize: '10px', fontWeight: '600' }}>
                      Section {index + 1}
                    </div>
                    <div style={{ fontSize: '8px', opacity: 0.7 }}>
                      {section.frameCount} frames
                    </div>
                  </div>
                )}
              </div>
            </button>
          )
        })}
        
        {/* Dynamic padding to enable scrolling at any snap point */}
        <div style={{ height: `${paddingNeeded}px`, width: '100%' }} />
      </div>

      {/* Stats footer */}
      <div 
        className="sections-drawer__stats"
        style={{
          padding: '10px 16px',
          background: 'rgba(0, 0, 0, 0.4)',
          borderTop: '1px solid rgba(126, 231, 135, 0.15)',
          fontSize: '11px',
          color: 'rgba(255, 255, 255, 0.65)',
          textAlign: 'center'
        }}
      >
        Showing {displaySections.length} sections • {cameras.length} total frames • Snap: {snapPoint}
      </div>
    </div>
  )
}
